import { env } from "@/env";
import { hasLocalePrefix, IS_PROD_DEPLOYMENT } from "@mw/shared/translation";
import { UrlObject } from "url";

// Allowlist of safe protocols - only these are preserved, all others are sanitized
// This prevents XSS attacks from protocols like javascript:, data:, vbscript:
const SAFE_PROTOCOLS = ["https:", "http:", "mailto:", "tel:", "sms:"];

export function hasSafeProtocol(url: string): boolean {
  const lowerUrl = url.toLowerCase();
  return SAFE_PROTOCOLS.some(protocol => lowerUrl.startsWith(protocol));
}

const THIS_DOMAIN = [
  `${env.NEXT_PUBLIC_SERVER_URL.replace(/\/+$/, "")}/`,
  "https://elevenlabs.io/",
  "https://eleven2.dev/",
];

export function sanitizeUrl(href: string | UrlObject) {
  if (typeof href !== "string") {
    return href;
  }
  // Preserve anchor-only URLs (e.g., #section)
  if (href.startsWith("#")) {
    return href;
  }
  // Only preserve URLs with safe protocols (mailto:, tel:, sms:, https://, http://)
  // Dangerous protocols like javascript:, data:, vbscript: will fall through to path sanitization
  if (hasSafeProtocol(href)) {
    // Upgrade http:// to https:// in production
    if (IS_PROD_DEPLOYMENT && href.startsWith("http://")) {
      href = href.replace("http://", "https://");
    }

    // Make the link relative to the current domain
    for (const domain of THIS_DOMAIN) {
      const domainWithoutTrailingSlash = domain.endsWith("/") ? domain.slice(0, -1) : domain;

      if (
        href === domainWithoutTrailingSlash ||
        href.startsWith(`${domainWithoutTrailingSlash}/`) ||
        href.startsWith(`${domainWithoutTrailingSlash}?`) ||
        href.startsWith(`${domainWithoutTrailingSlash}#`)
      ) {
        href = href.slice(domainWithoutTrailingSlash.length);

        if (href.startsWith("/")) {
          return href;
        }
        return `/${href}`;
      }
    }

    return href;
  }
  if (href.startsWith("/")) {
    return href;
  }
  return `/${href}`;
}

export function getUrl(href: string | UrlObject) {
  const safeUrl = String(sanitizeUrl(href));

  // External URLs and anchors cannot be localized
  const isExternal =
    hasSafeProtocol(safeUrl) || safeUrl.startsWith("/docs") || safeUrl.startsWith("/app") || safeUrl.startsWith("/v1");

  const cannotLocalize = isExternal || safeUrl.startsWith("#") || hasLocalePrefix(safeUrl);

  return {
    url: safeUrl,
    cannotLocalize,
    isExternal,
  };
}
